// ============================================================
// ExpressFileUploadMulter.jsx
//
// A branded, responsive course content page for the
// "File Uploads with Multer" lesson.
//
// Author: ProDev Architecture Series (Storage Branch)
// Features: Disk storage configuration, single/multiple upload
//           logic, and navigation bridge to Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import CloudUploadIcon       from "@mui/icons-material/CloudUpload";
import StorageIcon            from "@mui/icons-material/Storage";
import AttachFileIcon         from "@mui/icons-material/AttachFile";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import SecurityIcon           from "@mui/icons-material/Security";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressFileUploadMulter() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Storage · Module 21"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        File Uploads <span>with Multer</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Handling files requires specialized middleware. Multer processes <code>multipart/form-data</code>, allowing your API to ingest images, PDFs, and documents while managing storage strategies efficiently.
                    </Typography>
                </Box>

                {/* 1. INSTALLATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Installation & Import" />
                    <CodeBlock lines={[
                        { cmd: "const multer = require('multer');", comment: "npm install multer" },
                    ]} />
                </Box>

                {/* 2. CONFIGURATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StorageIcon />} label="2. Disk Storage Engine" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Define where files go and how they are named. At ProDev, we always use unique timestamps to prevent filename collisions.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const storage = multer.diskStorage({", comment: "" },
                        { cmd: "  destination: (req, file, cb) => cb(null, 'uploads/'),", comment: "Folder must exist" },
                        { cmd: "  filename: (req, file, cb) => {", comment: "" },
                        { cmd: "    const uniqueSuffix = Date.now() + '-' + Math.round(Math.random() * 1E9);", comment: "" },
                        { cmd: "    cb(null, `${uniqueSuffix}-${file.originalname}`);", comment: "Rename file" },
                        { cmd: "  }", comment: "" },
                        { cmd: "});", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "const upload = multer({ storage });", comment: "Initialize instance" },
                    ]} />
                </Box>

                {/* 3. ROUTE IMPLEMENTATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CloudUploadIcon />} label="3. The Upload Route" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Apply Multer as route-specific middleware. Use <code style={{color: BRAND.white}}>.single('fieldName')</code> for one file or <code style={{color: BRAND.white}}>.array()</code> for many.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.post('/upload', upload.single('avatar'), (req, res) => {", comment: "" },
                        { cmd: "  if (!req.file) return res.status(400).send('No file uploaded');", comment: "" },
                        { cmd: "  // req.file contains all metadata (path, size, mimetype)", comment: "" },
                        { cmd: "  res.json({ url: `/uploads/${req.file.filename}` });", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 4. FRONTEND TEST */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<AttachFileIcon />} label="4. Testing with Multipart Form" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Ensure your HTML form uses the correct <code style={{color: BRAND.white}}>enctype</code>.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "<form action='/upload' method='POST' enctype='multipart/form-data'>", comment: "" },
                        { cmd: "  <input type='file' name='avatar' />", comment: "Name must match single('avatar')" },
                        { cmd: "  <button type='submit'>Upload Now</button>", comment: "" },
                        { cmd: "</form>", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="Security & Limits" />
                    <Grid container spacing={2}>
                        {[
                            { title: "File Size Limits", desc: "Use { limits: { fileSize: 1024 * 1024 } } to block massive uploads.", icon: <StarIcon /> },
                            { title: "Type Validation", desc: "Use fileFilter to only allow specific mimetypes like image/png.", icon: <CheckCircleOutlineIcon /> },
                            { title: "Static Hosting", desc: "Use express.static('uploads') to allow browser access to files.", icon: <CodeIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> For production apps, avoid storing files on your server's local disk. Use Multer engines like <code style={{ color: BRAND.goldBright }}>multer-s3</code> or <strong>Cloudinary</strong> to upload directly to a cloud storage bucket, ensuring your app remains stateless and scalable.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
